/**
 * Form page (CC_11, Feature #15 — D-59/D-60/D-61/D-66). Reached via the opaque
 * `/f/:formId` URL, OUTSIDE the auth hull: the SERVER decides per form whether
 * anonymous submits are allowed (public_submit) or a session + guard read is
 * required — this page just renders what the descriptor returns (401/403 → the
 * sign-in hint). Strictly submit-only: no list data is ever fetched here.
 *
 * R4 — Relation fields ARE now rendered, but SAFELY: a form field can carry a
 * CURATED option set (owner-published target rows), selectable by anyone
 * (anonymous included) without exposing the target list. For authenticated,
 * non-public forms a field may additionally enable a guard-filtered live search
 * (formRelationSearch). The submit sends id-only `links`; the server re-validates.
 */

import { useEffect, useRef, useState } from "react";

import { ApiError, ryfeApi } from "../api";

import { PublicChrome } from "./PublicChrome";

import "./ryfe.scss";

import type {
  FormDescriptor,
  FormDescriptorField,
  RelationOption,
} from "../api";

const asIdList = (v: unknown): string[] => {
  if (v === undefined || v === null || v === "") {
    return [];
  }
  return Array.isArray(v) ? v.map(String) : [String(v)];
};

export const RyfeForm = ({ formId }: { formId: string }) => {
  const [form, setForm] = useState<FormDescriptor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    ryfeApi
      .getForm(formId)
      .then((f) => {
        if (cancelled) {
          return;
        }
        setForm(f);
        // Prefill (D-60): fixed defaults from the view config — never URL query.
        const initial: Record<string, unknown> = {};
        for (const field of f.fields) {
          if (field.prefill_default !== undefined) {
            initial[field.column_id] = field.prefill_default;
          }
        }
        setValues(initial);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        if (
          err instanceof ApiError &&
          (err.status === 401 || err.status === 403)
        ) {
          setError(
            "Dieses Formular erfordert eine Anmeldung. Bitte zuerst einloggen.",
          );
        } else {
          setError("Formular nicht gefunden oder nicht verfügbar.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [formId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || busy) {
      return;
    }
    setBusy(true);
    setSubmitError(null);
    try {
      const data: Record<string, unknown> = {};
      const links: Record<string, string[]> = {};
      for (const field of form.fields) {
        if (field.type === "relation") {
          // R4 — id-only edges; the server re-validates against the curated set
          // (or, for authenticated forms, target-list read).
          const ids = asIdList(values[field.column_id]);
          if (ids.length) {
            links[field.column_id] = ids;
          }
          continue;
        }
        const v = values[field.column_id];
        if (v !== undefined && v !== null && String(v) !== "") {
          data[field.column_id] = v;
        }
      }
      await ryfeApi.submitForm(formId, {
        data,
        ...(Object.keys(links).length ? { links } : {}),
      });
      setDone(true);
    } catch (err) {
      setSubmitError(
        err instanceof ApiError
          ? err.message
          : "Senden fehlgeschlagen — bitte erneut versuchen.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="ryfe-form-page">
        <div className="ryfe-panel">
          <p className="ryfe-auth__error">{error}</p>
        </div>
      </div>
    );
  }
  if (!form) {
    return <div className="ryfe-muted">Formular lädt…</div>;
  }
  if (done) {
    return (
      <div className="ryfe-form-page">
        <div className="ryfe-panel">
          <h1 className="ryfe-panel__title">{form.name}</h1>
          <p role="status">Danke — der Eintrag wurde übermittelt.</p>
          <button
            className="ryfe-btn ryfe-btn--ghost"
            onClick={() => {
              setDone(false);
              setValues((v) => {
                const next: Record<string, unknown> = {};
                for (const field of form.fields) {
                  if (field.prefill_default !== undefined) {
                    next[field.column_id] = field.prefill_default;
                  }
                }
                return next ?? v;
              });
            }}
          >
            Weiteren Eintrag senden
          </button>
        </div>
      </div>
    );
  }

  const setValue = (columnId: string, v: unknown) =>
    setValues((prev) => ({ ...prev, [columnId]: v }));

  return (
    <PublicChrome
      title={form.name}
      badge="Formular"
      footer={form.publicSubmit ? "Öffentliches Formular." : undefined}
    >
      <div className="ryfe-form-page">
        <form className="ryfe-panel" onSubmit={submit}>
          <p className="ryfe-muted" style={{ padding: 0 }}>
            {form.listName}
          </p>
          {form.fields.map((field) => {
            const value = values[field.column_id];
            const common = {
              id: `f-${field.column_id}`,
              required: field.required,
            };
            // R4 — Relations-Feld: kuratierte Auswahl (+ optionale Live-Suche).
            if (field.type === "relation") {
              return (
                <RelationField
                  key={field.column_id}
                  formId={formId}
                  field={field}
                  publicSubmit={form.publicSubmit}
                  selected={asIdList(value)}
                  onChange={(ids) => setValue(field.column_id, ids)}
                />
              );
            }
            return (
              <label
                key={field.column_id}
                className="ryfe-field-label"
                htmlFor={common.id}
              >
                {field.name}
                {field.required ? " *" : ""}
                {field.type === "select" ? (
                  <select
                    {...common}
                    className="ryfe-select"
                    value={value === undefined ? "" : String(value)}
                    onChange={(e) => setValue(field.column_id, e.target.value)}
                  >
                    <option value="">—</option>
                    {(field.options || []).map((o, i) => {
                      const opt =
                        typeof o === "string"
                          ? { value: o, label: o }
                          : (o as { value?: string; label?: string });
                      const val = String(opt.value ?? opt.label ?? "");
                      return (
                        <option key={i} value={val}>
                          {String(opt.label ?? val)}
                        </option>
                      );
                    })}
                  </select>
                ) : field.type === "checkbox" ? (
                  <input
                    {...common}
                    required={false}
                    type="checkbox"
                    checked={value === true || value === "true"}
                    onChange={(e) =>
                      setValue(field.column_id, e.target.checked)
                    }
                  />
                ) : (
                  <input
                    {...common}
                    className="ryfe-field-input"
                    type={
                      field.type === "number"
                        ? "number"
                        : field.type === "date"
                        ? "date"
                        : field.type === "color"
                        ? "color"
                        : field.type === "url"
                        ? "url"
                        : "text"
                    }
                    value={value === undefined ? "" : String(value)}
                    onChange={(e) => setValue(field.column_id, e.target.value)}
                  />
                )}
              </label>
            );
          })}
          {submitError && (
            <p className="ryfe-auth__error" role="alert">
              {submitError}
            </p>
          )}
          <button
            className="ryfe-btn ryfe-btn--primary"
            type="submit"
            disabled={busy}
          >
            {busy ? "Sende…" : "Absenden"}
          </button>
        </form>
      </div>
    </PublicChrome>
  );
};

/**
 * R4 — ein Relations-Feld im Formular. Basis sind die kuratierten Optionen aus
 * dem Descriptor (für alle Absender, auch anonym, sicher). Ist Live-Suche aktiv
 * UND das Formular nicht öffentlich, kann ein angemeldeter Absender zusätzlich
 * guard-gefiltert in der Ziel-Liste suchen. Auswahl → id-only `links`.
 */
const RelationField = ({
  formId,
  field,
  publicSubmit,
  selected,
  onChange,
}: {
  formId: string;
  field: FormDescriptorField;
  publicSubmit: boolean;
  selected: string[];
  onChange: (ids: string[]) => void;
}) => {
  const curated = field.relation_options || [];
  const multiple = field.relation_multiple !== false;
  const liveEnabled = !publicSubmit && field.relation_live_search === true;
  const [live, setLive] = useState<RelationOption[]>([]);
  const [query, setQuery] = useState("");
  const reqRef = useRef(0);

  useEffect(() => {
    if (!liveEnabled || query.trim() === "") {
      return;
    }
    const seq = ++reqRef.current;
    ryfeApi
      .formRelationSearch(formId, field.column_id, query)
      .then((r) => {
        if (seq === reqRef.current) {
          setLive(r.items || []);
        }
      })
      .catch(() => {
        if (seq === reqRef.current) {
          setLive([]);
        }
      });
  }, [liveEnabled, formId, field.column_id, query]);

  // Curated + live-search results + any preselected id (so a prefilled value is
  // still shown even if it is not in the curated set), de-duplicated.
  const byId = new Map<string, RelationOption>();
  for (const o of curated) {
    byId.set(o.row_id, o);
  }
  for (const o of live) {
    if (!byId.has(o.row_id)) {
      byId.set(o.row_id, o);
    }
  }
  for (const id of selected) {
    if (!byId.has(id)) {
      byId.set(id, { row_id: id, display: id });
    }
  }
  const options = [...byId.values()];

  const toggle = (id: string) => {
    if (multiple) {
      onChange(
        selected.includes(id)
          ? selected.filter((x) => x !== id)
          : [...selected, id],
      );
    } else {
      onChange(selected.includes(id) ? [] : [id]);
    }
  };

  return (
    <div className="ryfe-field-label">
      <span>
        {field.name}
        {field.required ? " *" : ""}
      </span>
      {options.length === 0 ? (
        <span className="ryfe-muted">Keine Auswahl verfügbar.</span>
      ) : multiple ? (
        <div className="ryfe-relselect" role="group" aria-label={field.name}>
          {options.map((o) => (
            <label key={o.row_id} className="ryfe-check">
              <input
                type="checkbox"
                checked={selected.includes(o.row_id)}
                onChange={() => toggle(o.row_id)}
              />
              {o.display || o.row_id}
            </label>
          ))}
        </div>
      ) : (
        <select
          className="ryfe-select"
          value={selected[0] || ""}
          aria-label={field.name}
          onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
        >
          <option value="">—</option>
          {options.map((o) => (
            <option key={o.row_id} value={o.row_id}>
              {o.display || o.row_id}
            </option>
          ))}
        </select>
      )}
      {liveEnabled && (
        <input
          className="ryfe-field-input"
          placeholder="In der Ziel-Liste suchen…"
          aria-label={`${field.name} suchen`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
    </div>
  );
};
